import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth } from '@/lib/firebase/config';
import { signOut as firebaseSignOut, signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { UserRoles } from '@/lib/firebase/auth';
import { identifySignedInUser, resetIdentity } from '@/lib/utils/analytics';

interface AuthContextType {
  user: any;
  loading: boolean;
  userRoles: UserRoles | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userRoles: null,
  signIn: async () => { },
  signOut: async () => { },
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles | null>(null);
  const [hasSeenCreatorWelcome, setHasSeenCreatorWelcome] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const identifiedUidRef = useRef<string | null>(null);

  useEffect(() => {
    let unsubscribeFromUserDoc: (() => void) | null = null;

    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        // Tell the Control Center chat widget who this is, so it never asks a
        // signed-in member for an address we already have. Deliberately NOT
        // folded into the analytics identify below: that one is gated on cookie
        // consent, and someone who declined analytics should still not be made
        // to retype their email to get a reply. Guarded because the widget is a
        // deferred script and this listener usually wins the race — it drains
        // _pending on boot.
        try {
          const cc = ((window as unknown as { ccChat?: Record<string, unknown> }).ccChat ??= {}) as {
            identify?: (w: unknown) => void;
            _pending?: unknown;
          };
          if (authUser.email) {
            const who = { email: authUser.email, name: authUser.displayName ?? undefined };
            if (cc.identify) cc.identify(who);
            else cc._pending = who;
          }
        } catch {
          /* the chat is a nicety; never let it break auth */
        }
        if (identifiedUidRef.current !== authUser.uid) {
          identifiedUidRef.current = authUser.uid;
          identifySignedInUser(authUser.uid, { email: authUser.email ?? undefined });
        }
        try {
          // Set up real-time listener for user document
          unsubscribeFromUserDoc = onSnapshot(
            doc(db, 'users', authUser.uid),
            (userDoc) => {
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const roles = userData.roles as UserRoles;

                // The auth email changes out-of-band (verifyBeforeUpdateEmail
                // link); keep the doc the mailers read in step with it.
                if (authUser.email && authUser.emailVerified && userData.email !== authUser.email) {
                  updateDoc(doc(db, 'users', authUser.uid), { email: authUser.email }).catch(() => {});
                }

                // Merge Firestore data with Firebase Auth user
                const enrichedUser = {
                  ...authUser,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  displayName: userData.displayName || authUser.displayName,
                  photoURL: userData.photoURL || authUser.photoURL,
                };

                setUser(enrichedUser);
                setUserRoles(roles);
                setHasSeenCreatorWelcome(!!userData.hasSeenCreatorWelcome);

                // Only redirect on auth pages. NOT '/signup-creator': that page
                // awaits upgradeToCreator() itself and navigates explicitly once
                // it resolves. The user doc is created with roles.creator:false
                // first, so this listener fires early and raced navigate('/dashboard')
                // against the page's own (correct) navigate('/creator'), landing a
                // freshly-signed-up athlete on the wrong screen (TSF-251).
                const isAuthPage = location.pathname === '/signin' || location.pathname === '/signup';
                if (isAuthPage) {
                  if (roles.creator) {
                    navigate('/creator');
                  } else {
                    navigate('/dashboard');
                  }
                }
              } else {
                // Document doesn't exist yet (during signup), just use auth user
                setUser(authUser);
                setUserRoles(null);
              }
              setLoading(false);
            },
            (error) => {
              console.error('Error listening to user document:', error);
              // On error, still set the auth user and continue
              setUser(authUser);
              setUserRoles(null);
              setLoading(false);
            }
          );
        } catch (error) {
          console.error('Error setting up user listener:', error);
          setUser(authUser);
          setLoading(false);
        }
      } else {
        if (identifiedUidRef.current !== null) {
          identifiedUidRef.current = null;
          resetIdentity();
        }
        setUser(null);
        setUserRoles(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeFromUserDoc) {
        unsubscribeFromUserDoc();
      }
    };
  }, [navigate, location]);

  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, userRoles, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);